import React from 'react';
import { CellContext } from '@tanstack/react-table';
import { utcToZonedTime } from 'date-fns-tz';

import { AdaptiveTableCell, IconCell } from '@components/common';
import Actions from '@components/common/Actions';
import Spinner from '@components/common/Spinner';
import Tooltip from '@components/common/Tooltip';

import { isEmpty, uuid } from '@utils/common';
import { formatDate } from '@utils/kpi-utils/date';

import { IBrandingSetup } from '@interfaces/platform/branding';

import { defaultBrandingSetupName, formatTemplateWithTime, timezoneUTC } from '@constants/common';

import styles from './ManagePlatformBranding.module.scss';

const visibleOrganisationsNumber = 3;

interface IGetColumns {
	openEditModal: () => void;
	setEditableSetup: (editableSetup: IBrandingSetup | null) => void;
	openDeleteConfirm: () => void;
	setSingleIdForRemove: React.Dispatch<React.SetStateAction<string | null>>;
}

export const getColumns = ({
	openEditModal,
	setEditableSetup,
	openDeleteConfirm,
	setSingleIdForRemove,
}: IGetColumns) => ([
	{
		header: 'Setup name',
		accessorKey: 'name',
		cell: ({ getValue }: CellContext<any, any>) => {
			const name = getValue();

			return <AdaptiveTableCell text={name} />;
		},
	},
	{
		header: 'Logo icon',
		accessorKey: 'logo',
		enableSorting: false,
		cell: ({ getValue }: CellContext<any, any>) => {
			const logo = getValue();

			return (
				<IconCell
					id={logo?.id}
					isLogoSquare
					size='30px'
				/>
			);
		},
	},
	{
		header: 'Logo caption',
		accessorKey: 'logoCaption',
		enableSorting: false,
		cell: ({ getValue }: CellContext<any, any>) => {
			const logoCaption = getValue();

			return <AdaptiveTableCell text={logoCaption} />;
		},
	},
	{
		header: 'Spinner',
		accessorKey: 'spinner',
		enableSorting: false,
		cell: ({ row }: CellContext<any, any>) => {
			return (
				<div className={styles.spinnerColumn}>
					<Spinner
						size={30}
						spinnerColorId={row?.original?.spinnerColorId}
						spinnerId={row?.original?.spinnerId}
					/>
				</div>
			);
		},
	},
	{
		header: 'Organisations',
		accessorKey: 'organizations',
		enableSorting: false,
		cell: ({ getValue }: CellContext<any, any>) => {
			const organisations: { id: string; name: string }[] = getValue();
			const organisationsTooltip = organisations?.map(organisation => organisation?.name)?.join(', ');

			return (
				<Tooltip
					content={organisationsTooltip}
					isDisabled={isEmpty(organisationsTooltip)}
					placement='auto'
				>
					<div className={styles.organisationsColumn}>
						{organisations?.filter((item, index) => index <= visibleOrganisationsNumber)
							?.map((organisation, index) => {
								return (
									index < visibleOrganisationsNumber
										? <div key={`${organisation?.id}-${uuid()}`}>
											{`${organisation?.name}`}
										</div>
										: '...'
								);
							})}
					</div>
				</Tooltip>
			);
		},
	},
	{
		header: 'Created',
		accessorKey: 'createdAt',
		cell: ({ getValue }: CellContext<any, any>) => {
			const createdAt = getValue();
			const date = createdAt ? formatDate(utcToZonedTime(createdAt, timezoneUTC), formatTemplateWithTime) : '';

			return <AdaptiveTableCell text={date} />;
		},
	},
	{
		header: 'Updated',
		accessorKey: 'updatedAt',
		cell: ({ getValue }: CellContext<any, any>) => {
			const updatedAt = getValue();
			const date = updatedAt ? formatDate(utcToZonedTime(updatedAt, timezoneUTC), formatTemplateWithTime) : '';

			return <AdaptiveTableCell text={date} />;
		},
	},
	{
		header: 'Actions',
		accessorKey: 'actions',
		enableSorting: false,
		cell: ({ row }: CellContext<any, any>) => {
			const editHandler = () => {
				setEditableSetup(row.original);

				openEditModal();
			};

			const handleDelete = () => {
				setSingleIdForRemove(row.original.id);
				openDeleteConfirm();
			};

			const isDeleteDisabled = row.original.name === defaultBrandingSetupName;

			return (
				<Actions
					deleteTooltip='Delete setup'
					editHandler={editHandler}
					editTooltip='Edit setup'
					handleDelete={handleDelete}
					isDelete
					isDeleteDisable={isDeleteDisabled}
					isEdit
				/>
			);
		},
	},
]);
